import { createEntityAdapter, createSelector, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit';
import { FileResult } from '../../api';
import { DocumentCategory, DocumentCategoryNormalized, DocumentHeader } from '../../api/models/documents.models';
import { getSortComparer } from '../../helpers';
import { getNormalizedDocumentCategories } from '../../helpers/general/document';
import { RootState } from '../store';

const documentCategoriesAdapter = createEntityAdapter<DocumentCategoryNormalized>({
  selectId: (data: DocumentCategoryNormalized) => data.DocumentCategoryId,
});

const documentHeadersAdapter = createEntityAdapter<DocumentHeader>({
  selectId: (data: DocumentHeader) => data.DocumentHeaderId,
});

interface DocumentsState {
  isLoadingDocumentHeaders: boolean;
  documentHeaders: EntityState<DocumentHeader>;

  isLoadingDocumentCategories: boolean;
  documentCategories: EntityState<DocumentCategoryNormalized>;

  isLoadingDocumentDetailFile: boolean;
  documentDetailFile: FileResult | undefined;

  highlightedCategoryId?: string;
}

const initialState: DocumentsState = {
  isLoadingDocumentHeaders: false,
  documentHeaders: documentHeadersAdapter.getInitialState(),

  isLoadingDocumentCategories: false,
  documentCategories: documentCategoriesAdapter.getInitialState(),

  isLoadingDocumentDetailFile: false,
  documentDetailFile: undefined,

  highlightedCategoryId: undefined,
};

export const documentsSlice = createSlice({
  name: 'documents',
  initialState: initialState,
  reducers: {
    resetState: () => initialState,
    resetDocuments: (state: DocumentsState) => {
      state.documentHeaders = documentHeadersAdapter.getInitialState();
    },
    setIsLoadingDocumentHeaders: (state: DocumentsState, action: PayloadAction<boolean>) => {
      state.isLoadingDocumentHeaders = action.payload;
    },
    setDocumentHeaders: (
      state: DocumentsState,
      action: PayloadAction<{ documents: DocumentHeader[]; documentCategoryId: string }>
    ) => {
      const category = state.documentCategories.entities[action.payload.documentCategoryId];
      if (category) {
        category.DocumentHeaderIds = action.payload.documents.map((x) => x.DocumentHeaderId);
        category.HasLoadedDocumentHeaders = true;
      }
      documentHeadersAdapter.addMany(state.documentHeaders, action.payload.documents);
    },
    setIsLoadingDocumentCategories: (state: DocumentsState, action: PayloadAction<boolean>) => {
      state.isLoadingDocumentCategories = action.payload;
    },
    setDocumentCategories: (state: DocumentsState, action: PayloadAction<DocumentCategory[]>) => {
      const { categories } = getNormalizedDocumentCategories(action.payload);
      documentCategoriesAdapter.setAll(state.documentCategories, categories);
    },
    toggleCategoryExpanded: (state: DocumentsState, action: PayloadAction<string>) => {
      const categoryId = action.payload;
      const category = state.documentCategories.entities[categoryId];
      if (category) {
        category.IsExpanded = !category.IsExpanded;
      }
    },
    collapseAllCategories: (state: DocumentsState) => {
      Object.values(state.documentCategories.entities).forEach((category) => {
        if (category) {
          category.IsExpanded = false;
        }
      });
    },
    expandParentCategories: (state: DocumentsState, action: PayloadAction<string>) => {
      const categoryId = action.payload;
      const categoryDict = state.documentCategories.entities;

      const expandParentsRecursively = (catId: string) => {
        const category = categoryDict[catId];
        if (category) {
          category.IsExpanded = true;
          if (category.ParentDocumentCategoryId) {
            expandParentsRecursively(category.ParentDocumentCategoryId);
          }
        }
      };

      expandParentsRecursively(categoryId);
    },
    setHighlightedCategory: (state: DocumentsState, action: PayloadAction<string | undefined>) => {
      state.highlightedCategoryId = action.payload;
    },
    setIsLoadingDocumentDetailFile: (state: DocumentsState, action: PayloadAction<boolean>) => {
      state.isLoadingDocumentDetailFile = action.payload;
    },
    setDocumentDetailFile: (state: DocumentsState, action: PayloadAction<any>) => {
      state.documentDetailFile = action.payload;
    },
  },
});

export const {
  selectAll: selectAllDocumentHeaders,
  selectIds: selectAllDocumentHeaderIds,
  selectById: selectDocumentHeaderById,
} = documentHeadersAdapter.getSelectors<EntityState<DocumentHeader>>((state: EntityState<DocumentHeader>) => state);

export const {
  selectAll: selectAllDocumentCategories,
  selectIds: selectAllDocumentCategoryIds,
  selectById: selectDocumentCategoryById,
} = documentCategoriesAdapter.getSelectors<EntityState<DocumentCategoryNormalized>>(
  (state: EntityState<DocumentCategoryNormalized>) => state
);

export const selectRootDocumentCategories = createSelector([selectAllDocumentCategories], (categories) => {
  return categories.filter((category) => !category.ParentDocumentCategoryId && category.Level === 0);
});

const selectCategoryDict = (s: RootState) => s.documents.documentCategories.entities;

export const selectDocumentCategoriesGridData = createSelector([selectCategoryDict], (categoryDict) => {
  const result: DocumentCategoryNormalized[] = [];
  const hiddenSet = new Set<string>();

  const populateResult = (categoryId: string) => {
    const cat = categoryDict?.[categoryId];

    if (!cat) return;

    if (!cat.IsExpanded || hiddenSet.has(cat.DocumentCategoryId)) {
      cat.ChildDocumentCategoryIds.forEach((y) => {
        hiddenSet.add(y);
      });
    }

    if (!hiddenSet.has(cat.DocumentCategoryId)) {
      result.push(cat);
      cat.ChildDocumentCategoryIds.forEach(populateResult);
    }
  };

  Object.values(categoryDict)
    .filter(
      (category): category is DocumentCategoryNormalized => category != null && !category.ParentDocumentCategoryId
    )
    .sort(getSortComparer((category) => category.DocumentCategorySequence))
    .forEach((category) => populateResult(category.DocumentCategoryId));

  return result;
});

export const selectFilteredDocumentHeaders = createSelector(
  [
    (state: any) => selectAllDocumentHeaders(state.documents.documentHeaders),
    (state: any, categoryId: string, searchText?: string) => ({ categoryId, searchText }),
  ],
  (allHeaders, { categoryId, searchText }) => {
    const trimmedSearchText = searchText?.trim() || '';

    if (trimmedSearchText) {
      return allHeaders.filter((doc: DocumentHeader) =>
        doc.DocumentHeaderTitle.toLowerCase().includes(trimmedSearchText.toLowerCase())
      );
    }

    if (categoryId && categoryId.trim()) {
      return allHeaders.filter((header) => header.DocumentCategoryId === categoryId);
    }
    return [];
  }
);
